import { latToTileY, lonToTileX } from '../../../map/services/imagerySources'
import { DENSITY_GEO, FOOTER_H, HEADER_H } from '../../components/pdf/pageGeometry'
import type { Density, LatLng, SiteMapLayout, StandardScale } from './types'

/**
 * Printed geometry for site maps (009, research R1/R2). Legibility is defined
 * once, in printed points, and shared by the single map, the overview labels
 * and the detail sheets. Pure — no canvas, no DOM, no react-pdf.
 */

/** Badge text size, bold white on the marker colour (FR-008) */
export const BADGE_TEXT_PT = 7
/** Badge height: a circle for 1–2 chars, a pill beyond */
export const BADGE_HEIGHT_PT = 11
/** Padding each side of the badge text */
export const BADGE_PAD_PT = 3
/** Minimum clear space between two badges */
export const BADGE_GAP_PT = 1
/** Glyph width estimate in em: Helvetica-Bold digits are 0.556 em, plus margin */
export const GLYPH_EM = 0.6
/** Detail-sheet canvas resolution (R8; 1.5 is the size reserve) */
export const PX_PER_PT = 2
export const STANDARD_SCALES: readonly StandardScale[] = [500, 1000, 2000, 5000]
/** Coverage-only last resort (R4 step 6) — never chosen for legibility */
export const COVERAGE_SCALE: StandardScale = 10000
export const MAX_SHEETS = 12
/** Sheet header strip above the map frame on a landscape sheet page */
export const SHEET_HEADER_PT = 30
/** Clearance kept between a tree's badge and the frame edge */
export const FRAME_MARGIN_PT = 6

/** Single map layout size in logical px (the 1400 × 900 canvas) */
export const SINGLE_MAP_WIDTH = 1400
export const SINGLE_MAP_HEIGHT = 900
export const TILE_SIZE = 256
const MIN_ZOOM = 3
/** Edge padding so badges centred on a tree never sit on the canvas border */
const SINGLE_MAP_PADDING_PX = 48
/** The single map prints at `assetImage.maxHeight` (chrome.tsx) when height-bound */
const SINGLE_MAP_MAX_HEIGHT_PT = 320

const A4_LONG_PT = 842
const A4_SHORT_PT = 595
/** Metres per printed point */
const METRES_PER_PT = 0.000352778
/** Web Mercator ground resolution at zoom 0 on the equator, m/px */
const MERCATOR_M_PER_PX_Z0 = 156543.03

const MAX_LABEL_CHARS = 16

/** Badge text as drawn: trimmed, long tags cut to 16 chars with an ellipsis */
export function truncateLabel(label: string): string {
  const trimmed = label.trim()
  return trimmed.length > MAX_LABEL_CHARS ? `${trimmed.slice(0, MAX_LABEL_CHARS - 1)}…` : trimmed
}

const LAYOUTS: readonly SiteMapLayout[] = ['auto', 'single', 'book']

/** Unknown/absent ⇒ 'auto'. Used on every read of report_data.site_map_layout. */
export function parseSiteMapLayout(value: unknown): SiteMapLayout {
  return LAYOUTS.includes(value as SiteMapLayout) ? (value as SiteMapLayout) : 'auto'
}

/** Printed badge footprint in pt for a label (R1). */
export function badgeSizePt(
  label: string,
  textPt: number = BADGE_TEXT_PT
): { w: number; h: number } {
  const h = (BADGE_HEIGHT_PT * textPt) / BADGE_TEXT_PT
  const pad = (BADGE_PAD_PT * textPt) / BADGE_TEXT_PT
  return { w: Math.max(h, label.length * GLYPH_EM * textPt + 2 * pad), h }
}

/** Badge rect (pt) centred on a point */
export function badgeRect(
  center: { x: number; y: number },
  label: string,
  textPt: number = BADGE_TEXT_PT
): { x: number; y: number; w: number; h: number } {
  const { w, h } = badgeSizePt(label, textPt)
  return { x: center.x - w / 2, y: center.y - h / 2, w, h }
}

/** Two badge rects intersect once each is inflated by half the gap */
export function rectsCollide(
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number }
): boolean {
  const g = BADGE_GAP_PT
  return a.x < b.x + b.w + g && b.x < a.x + a.w + g && a.y < b.y + b.h + g && b.y < a.y + a.h + g
}

/**
 * True when no two badges (pt rects centred on their anchors) intersect.
 * Uniform-grid spatial hash with cell = widest badge, so each badge is only
 * compared with its 3 × 3 neighbourhood — O(n) for realistic sites.
 */
export function badgesLegible(
  anchorsPt: Array<{ x: number; y: number; label: string }>,
  textPt: number = BADGE_TEXT_PT
): boolean {
  if (anchorsPt.length < 2) return true
  const badges = anchorsPt.map((a) => ({ a, rect: badgeRect(a, a.label, textPt) }))
  const cell = Math.max(...badges.map(({ rect }) => Math.max(rect.w, rect.h))) + BADGE_GAP_PT
  const grid = new Map<string, typeof badges>()
  for (const b of badges) {
    const cx = Math.floor(b.a.x / cell)
    const cy = Math.floor(b.a.y / cell)
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (const o of grid.get(`${cx + dx},${cy + dy}`) ?? []) {
          if (rectsCollide(b.rect, o.rect)) return false
        }
      }
    }
    const key = `${cx},${cy}`
    const bucket = grid.get(key)
    if (bucket) bucket.push(b)
    else grid.set(key, [b])
  }
  return true
}

/** Fractional map zoom at which a sheet rendered at PX_PER_PT prints at 1:scale (R2). */
export function zoomForScale(scale: StandardScale, centerLatitude: number): number {
  const cos = Math.cos((centerLatitude * Math.PI) / 180)
  return Math.log2((MERCATOR_M_PER_PX_Z0 * cos * PX_PER_PT) / (METRES_PER_PT * scale))
}

/** Landscape detail-sheet map frame for a density (R2): the landscape A4
    content box less the sheet header strip. */
export function sheetFramePt(density: Density): { widthPt: number; heightPt: number } {
  const g = DENSITY_GEO[density]
  return {
    widthPt: A4_LONG_PT - 2 * g.padH,
    heightPt: A4_SHORT_PT - (g.padTop + HEADER_H) - (g.padBottom + FOOTER_H) - SHEET_HEADER_PT,
  }
}

/**
 * Printed pt per logical canvas px of the single 1400 × 900 site map. The
 * image is `width: 100%` of the portrait content box but capped at
 * `assetImage.maxHeight` (320 pt, chrome.tsx) — whichever binds first.
 */
export function singleMapPtPerPx(density: Density): number {
  const contentWidth = A4_SHORT_PT - 2 * DENSITY_GEO[density].padH
  return Math.min(contentWidth / SINGLE_MAP_WIDTH, SINGLE_MAP_MAX_HEIGHT_PT / SINGLE_MAP_HEIGHT)
}

/**
 * Deepest (fractional) zoom ≤ maxZoom at which all framing points fit inside
 * the padded single-map canvas. Fractional, so the site fills the frame
 * instead of stopping at the last whole zoom level.
 */
export function fitZoom(points: LatLng[], maxZoom: number): number {
  const xs = points.map((p) => lonToTileX(p.longitude, 0) * TILE_SIZE)
  const ys = points.map((p) => latToTileY(p.latitude, 0) * TILE_SIZE)
  const spanX = Math.max(...xs) - Math.min(...xs)
  const spanY = Math.max(...ys) - Math.min(...ys)
  const zoomX =
    spanX > 0 ? Math.log2((SINGLE_MAP_WIDTH - SINGLE_MAP_PADDING_PX * 2) / spanX) : Infinity
  const zoomY =
    spanY > 0 ? Math.log2((SINGLE_MAP_HEIGHT - SINGLE_MAP_PADDING_PX * 2) / spanY) : Infinity
  return Math.max(MIN_ZOOM, Math.min(maxZoom, zoomX, zoomY))
}

/** World pixel position of a point at a (fractional) zoom */
export function worldPx(p: LatLng, zoom: number): { x: number; y: number } {
  return {
    x: lonToTileX(p.longitude, zoom) * TILE_SIZE,
    y: latToTileY(p.latitude, zoom) * TILE_SIZE,
  }
}

// ---------------------------------------------------------------------------
// Sheet furniture (locator inset, scale bar, imagery credit). The planner
// reserves a corner for each so none of them covers a tree (009 follow-up).
// ---------------------------------------------------------------------------

/** Inset from the frame edge for the locator and scale bar */
export const FURNITURE_MARGIN_PT = 6
/** Clear space kept between furniture and any tree, badge or leader */
export const FURNITURE_GAP_PT = 2
const LOCATOR_WIDTH_FRAC = 0.16
const LOCATOR_HEIGHT_FRAC = 0.22
const SCALE_BAR_BOX_H_PT = 20
const SCALE_BAR_LABEL_PT = 7
/** Imagery credit: 6.5 pt text in an 11 pt strip with 3.5 pt padding each side */
export const ATTRIBUTION_TEXT_PT = 6.5
const ATTRIBUTION_STRIP_PT = 11

/**
 * Scale-bar length in metres: a 1/2/5 × 10ⁿ value at most a quarter of the
 * frame's ground width. The steps are ≤ 2.5× apart, so the bar always lands
 * at 10–25% of the frame.
 */
export function scaleBarLength(frameWidthM: number): number {
  const target = frameWidthM * 0.25
  const decade = 10 ** Math.floor(Math.log10(target))
  for (const m of [5, 2, 1]) if (m * decade <= target) return m * decade
  return decade / 2
}

/** Scale bar box: padding, the bar, its "N m" label */
export function scaleBarBoxPt(
  frame: { widthPt: number },
  scale: number
): { w: number; h: number; metres: number; barPt: number } {
  const metresPerPt = METRES_PER_PT * scale
  const metres = scaleBarLength(frame.widthPt * metresPerPt)
  const barPt = metres / metresPerPt
  const labelPt = `${metres} m`.length * GLYPH_EM * SCALE_BAR_LABEL_PT
  return { w: 4 + barPt + 4 + labelPt + 4, h: SCALE_BAR_BOX_H_PT, metres, barPt }
}

export function locatorBoxPt(frame: { widthPt: number; heightPt: number }): {
  w: number
  h: number
} {
  return { w: frame.widthPt * LOCATOR_WIDTH_FRAC, h: frame.heightPt * LOCATOR_HEIGHT_FRAC }
}

/** Credit strip for the longest text it may carry (glyph estimate errs wide) */
export function attributionBoxPt(maxChars: number): { w: number; h: number } {
  return { w: maxChars * GLYPH_EM * ATTRIBUTION_TEXT_PT + 7, h: ATTRIBUTION_STRIP_PT }
}
